/**
 * Storage System
 * 
 * Handles dual storage: localStorage (for non-authenticated users) 
 * and Shopify Pet Metaobjects (for authenticated customers)
 */

class PetFlowStorage {
  constructor(flowName = 'wizard-1') {
    this.flowName = flowName;
    this.localStorageKey = `${flowName}-pet-data`;
    this.localStorageKeyPets = `${flowName}-pets-list`; // List of all pet names
  }

  /**
   * Save state to localStorage
   * @param {Object} state - State object to save
   */
  saveToLocalStorage(state) {
    try {
      const currentCountry = (typeof window !== 'undefined' && window.__wizard1CurrentCountry) || '';
      const stateToSave = { ...state, _savedCountry: currentCountry };
      const stateString = JSON.stringify(stateToSave);
      localStorage.setItem(this.localStorageKey, stateString);
      
      // Also maintain list of pet names for duplicate checking
      // Only add to list if pet has been completed (has metaobjectId) or reached email step
      // This prevents adding names during active creation on the name step
      if (stateToSave.petData && stateToSave.petData.name) {
        // Only add to petsList if:
        // 1. Pet has a metaobjectId (completed and saved to Shopify), OR
        // 2. Pet has reached email step or beyond (significant progress, likely to be completed)
        const shouldAddToList = stateToSave.petMetaobjectId || 
                                (stateToSave.currentStep && ['email', 'calculation', 'flavors', 'plan', 'bulk'].includes(stateToSave.currentStep));
        
        if (shouldAddToList) {
          this.addPetNameToList(state.petData.name);
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      return false;
    }
  }

  /**
   * Load state from localStorage
   * @returns {Object|null} State object or null if not found
   */
  loadFromLocalStorage() {
    try {
      const stateString = localStorage.getItem(this.localStorageKey);
      if (!stateString) {
        return null;
      }
      
      return JSON.parse(stateString);
    } catch (error) {
      console.error('Error loading from localStorage:', error);
      return null;
    }
  }

  /**
   * Clear localStorage data
   */
  clearLocalStorage() {
    try {
      localStorage.removeItem(this.localStorageKey);
      localStorage.removeItem(this.localStorageKeyPets);
      return true;
    } catch (error) {
      console.error('Error clearing localStorage:', error);
      return false;
    }
  }

  /**
   * Add pet name to list (for duplicate checking)
   * @param {string} petName - Pet name
   */
  addPetNameToList(petName) {
    try {
      const petsList = this.getPetNamesList();
      const key = typeof petName === 'string' ? petName.trim().toLowerCase() : '';
      if (!key || petsList.includes(key)) return;
      petsList.push(key);
      localStorage.setItem(this.localStorageKeyPets, JSON.stringify(petsList));
    } catch (error) {
      console.error('Error adding pet name to list:', error);
    }
  }

  /**
   * Get list of pet names from localStorage
   * @returns {Array<string>} Array of pet names
   */
  getPetNamesList() {
    try {
      const petsListString = localStorage.getItem(this.localStorageKeyPets);
      return petsListString ? JSON.parse(petsListString) : [];
    } catch (error) {
      console.error('Error getting pet names list:', error);
      return [];
    }
  }

  /**
   * Check if pet name already exists in localStorage or Shopify
   * Only returns a match if it's a DIFFERENT pet (not the current one being created)
   * @param {string} petName - Pet name to check
   * @param {Object} options - Options
   * @param {Object} options.flow - Flow controller instance (for accessing Shopify API)
   * @returns {Promise<Object|null>} Pet info if exists (with source and step), null otherwise
   */
  async petNameExists(petName, options = {}) {
    if (!petName) return null;
    
    const normalizedName = petName.toLowerCase().trim();
    const currentState = options.flow?.state;
    const currentPetName = currentState?.petData?.name;
    const currentMetaobjectId = currentState?.petMetaobjectId;
    
    // Check Shopify FIRST (higher priority - these are confirmed existing pets)
    // Use customerId from options if provided, otherwise from flow state
    const customerIdToCheck = options.customerId || (options.flow && options.flow.state && options.flow.state.customerId);
    
    if (customerIdToCheck) {
      try {
        const shopifyPets = await options.flow.shopifyAPI.getAllPetMetaobjects(customerIdToCheck);
        
        for (const pet of shopifyPets) {
          if (pet.petName && pet.petName.toLowerCase() === normalizedName) {
            // CRITICAL: Don't match if it's the current pet being edited
            // If current metaobject ID matches, this is the same pet - not a duplicate
            if (currentMetaobjectId && pet.id === currentMetaobjectId) {
              continue; // Skip - this is the current pet
            }
            
            // Determine step from data completeness
            let step = 'plan'; // Default to plan if pet metaobject exists (means they completed flow)
            if (pet.fields) {
              const hasCalculations = pet.fields.some(f => f.key === 'dailyfoodg' && f.value);
              step = hasCalculations ? 'plan' : 'email';
            }
            
            return {
              name: pet.petName,
              step: step,
              source: 'shopify',
              metaobjectId: pet.id
            };
          }
        }
      } catch (error) {
        console.error('Error checking Shopify pets for duplicate name:', error);
        // Continue - check localStorage as fallback
      }
    }
    
    // Check localStorage (only if no Shopify match found)
    // For localStorage, we need to check if there's a PREVIOUS pet session with this name
    // NOT the current one being created
    const savedState = this.loadFromLocalStorage();
    
    // If saved state exists and has the same name, check if it's different from current
    if (savedState && savedState.petData && savedState.petData.name) {
      const savedName = savedState.petData.name.toLowerCase();
      
      if (savedName === normalizedName) {
        // It's a match by name, but is it a different pet?
        // Consider it a duplicate ONLY if:
        // 1. Current pet has no metaobjectId (new pet) AND saved state has a metaobjectId (completed pet), OR
        // 2. Current pet has a different metaobjectId, OR
        // 3. Current pet name is different (user is creating a NEW pet with old name), OR
        // 4. We're at the name step and haven't created a metaobject yet (new session)
        
        const isCurrentPetNew = !currentMetaobjectId;
        const savedHasMetaobject = !!savedState.petMetaobjectId;
        const isDifferentPet = savedState.petMetaobjectId !== currentMetaobjectId;
        const isNewSessionWithOldName = isCurrentPetNew && savedHasMetaobject;
        
        // Only return as duplicate if it's clearly a different pet
        if (isDifferentPet || isNewSessionWithOldName) {
          return {
            name: savedState.petData.name,
            step: savedState.currentStep || 'name',
            source: 'localStorage'
          };
        }
        
        // If names match but it's the same pet (same metaobjectId or same session), not a duplicate
        // This handles the case where user is editing current pet
      }
    }
    
    // Check petsList as additional check (for edge cases)
    // But only if we haven't found a match yet
    const petsList = this.getPetNamesList();
    if (petsList.includes(normalizedName)) {
      // Only consider it a duplicate if:
      // - Current pet name is different (user creating new pet with existing name), OR
      // - Current pet has no metaobjectId and we're not in the middle of creating it
      const isDifferentName = currentPetName && currentPetName.toLowerCase() !== normalizedName;
      const isNewPetCreation = !currentMetaobjectId && (!currentPetName || currentPetName.toLowerCase() !== normalizedName);
      
      if (isDifferentName || (isNewPetCreation && savedState && savedState.petMetaobjectId)) {
        // There's a previous pet with this name
        if (savedState && savedState.petData && savedState.petData.name && 
            savedState.petData.name.toLowerCase() === normalizedName) {
          // Only return if it's a different pet
          if (savedState.petMetaobjectId !== currentMetaobjectId) {
            return {
              name: savedState.petData.name,
              step: savedState.currentStep || 'name',
              source: 'localStorage'
            };
          }
        }
      }
    }
    
    return null;
  }

  /**
   * Get customerId for a given email from localStorage
   * Searches all localStorage entries (case-insensitive) and returns the most recent customerId
   * @param {string} email - Email address to search for
   * @returns {string|null} Customer ID or null if not found
   */
  getCustomerIdForEmail(email) {
    if (!email) return null;
    
    const normalizedEmail = email.toLowerCase().trim();
    const savedState = this.loadFromLocalStorage();
    
    // Check current saved state first (most recent)
    if (savedState && savedState.petData && savedState.petData.ownerEmail) {
      if (savedState.petData.ownerEmail.toLowerCase() === normalizedEmail) {
        // Prefer sessionCustomerId if available, otherwise customerId
        if (savedState.sessionCustomerId) {
          return savedState.sessionCustomerId;
        }
        if (savedState.customerId) {
          return savedState.customerId;
        }
      }
    }
    
    // Check all localStorage keys for this flow
    // Note: We can only check the current localStorage key, but that's the most recent session
    // For multiple sessions, we'd need to store a list, but for now this handles the common case
    return null;
  }

  /**
   * Check if email exists in localStorage
   * @param {string} email - Email address to check
   * @returns {boolean} True if email exists in localStorage
   */
  emailExistsInStorage(email) {
    if (!email) return false;
    
    const normalizedEmail = email.toLowerCase().trim();
    const savedState = this.loadFromLocalStorage();
    
    if (savedState && savedState.petData && savedState.petData.ownerEmail) {
      return savedState.petData.ownerEmail.toLowerCase() === normalizedEmail;
    }
    
    return false;
  }

  /**
   * Get all saved pets from localStorage
   * @returns {Array<Object>} Array of saved pet states
   */
  getAllSavedPets() {
    // For now, we only store one active pet in localStorage
    // In the future, this could be extended to store multiple pets
    const state = this.loadFromLocalStorage();
    if (state && state.petData && state.petData.name) {
      return [{
        name: state.petData.name,
        step: state.currentStep,
        state: state
      }];
    }
    return [];
  }


  /**
   * Auto-save state (saves to localStorage immediately, Shopify when customer exists)
   * Hybrid strategy: localStorage on every change, Shopify on step completion/debounced
   * @param {Object} state - State object to save
   * @param {string|null} customerId - Shopify customer ID (if available)
   * @param {Object} options - Options for saving behavior
   * @param {boolean} options.forceShopifySave - Force immediate Shopify save (e.g., on step completion)
   */
  async autoSave(state, customerId = null, options = {}) {
    // Always save to localStorage first (for all users, immediate)
    this.saveToLocalStorage(state);
    
    // Hybrid saving strategy:
    // - Before customer creation: Only localStorage
    // - After customer + Pet Metaobject creation: localStorage + Shopify (debounced or on step completion)
    
    // CRITICAL FIX: Don't save to Shopify on name step - customer and pet don't exist yet
    const currentStep = state.currentStep || 'name';
    if (currentStep === 'name') {
      return; // Skip Shopify save on name step
    }

    const nextStep = options?.nextStep || currentStep;

    // Pre-email: Pet metaobject exists but no customer yet - use pet-metaobject endpoint
    if (state.petMetaobjectId && state.petData?.name && !customerId && options?.forceShopifySave) {
      try {
        const flow = window.wizard1Flow;
        if (flow?.shopifyAPI?.backendApiUrl) {
          await flow.shopifyAPI.createOrUpdatePetMetaobject({
            petData: state.petData,
            calculations: state.calculations || {},
            petMetaobjectId: state.petMetaobjectId,
            resumeStep: nextStep,
            selectedFlavors: state.selectedFlavors || null
          });
        }
      } catch (e) {
        console.warn('Pet metaobject update failed:', e.message);
      }
      return;
    }
    
    if (customerId && state.petData && state.petData.name && state.petMetaobjectId) {
      // Customer and Pet Metaobject exist - save to Shopify
      if (options.forceShopifySave) {
        await this.saveToShopifyImmediate(state, customerId, state.petMetaobjectId, options);
      } else {
        this.saveToShopifyDebounced(state, customerId, state.petMetaobjectId);
      }
    }
  }

  /**
   * Save to Shopify immediately (no debounce)
   * Uses backend API instead of Storefront API to avoid 403 errors
   * @param {Object} state - State object
   * @param {string} customerId - Customer ID
   * @param {string} petMetaobjectId - Pet Metaobject ID
   * @param {Object} options - Options (e.g. nextStep for resume_step)
   */
  async saveToShopifyImmediate(state, customerId, petMetaobjectId, options = {}) {
    // CRITICAL FIX: Declare petData outside try block so it's available in catch block
    let petData = null;
    
    try {
      // Get flow controller reference (passed via constructor or accessed via global)
      const flow = window.wizard1Flow;
      if (!flow || !flow.shopifyAPI) {
        console.warn('Flow or ShopifyAPI not available for auto-save');
        return;
      }

      // CRITICAL FIX: Check if petData exists before accessing it
      if (!state || !state.petData) {
        console.warn('State or petData is missing, skipping Shopify save');
        return;
      }

      petData = state.petData;
      const calculations = state.calculations || {};

      // Use backend API to update (it handles updates when petMetaobjectId is provided)
      const customerData = {
        ownerEmail: petData.ownerEmail || '',
        ownerName: petData.ownerName || '',
        emailConsent: petData.emailConsent || false
      };

      const extraOptions = {
        resumeStep: options?.nextStep || null,
        selectedFlavors: state.selectedFlavors || null,
        cartItems: state.cartItems || null
      };
      const result = await flow.shopifyAPI.createCustomerViaBackend(
        customerData,
        petData,
        calculations,
        petMetaobjectId,
        null,
        extraOptions
      );

      if (result && result.petMetaobjectId) {
        console.log('Pet Metaobject updated via backend API:', result.petMetaobjectId);
      } else {
        console.warn('Backend API returned but no petMetaobjectId in response');
      }
    } catch (error) {
      // Log error with details for debugging
      console.error('Error saving to Shopify:', error);
      console.error('Error details:', {
        message: error.message,
        petMetaobjectId: petMetaobjectId,
        petName: petData?.name || 'N/A'
      });
      // Non-critical - log as warning, don't throw
      console.warn('Warning: Failed to save to Shopify (will retry later):', error.message);
      // Don't throw - just log and continue
    }
  }

  /**
   * Save to Shopify with debounce (5 seconds)
   * @param {Object} state - State object
   * @param {string} customerId - Customer ID
   * @param {string} petMetaobjectId - Pet Metaobject ID
   */
  saveToShopifyDebounced(state, customerId, petMetaobjectId) {
    // Clear existing debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Set new debounce timer (5 seconds)
    this.debounceTimer = setTimeout(async () => {
      await this.saveToShopifyImmediate(state, customerId, petMetaobjectId);
      this.debounceTimer = null;
    }, 5000);
  }

}

// Export for global access
window.PetFlowStorage = PetFlowStorage;

